import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface WeatherData {
  timestamp: string;
  irradiance: number;
  temperature: number;
  wind_speed: number;
  humidity: number;
}

interface WeatherTrendsChartProps {
  data: WeatherData[];
  loading?: boolean;
}

const WeatherTrendsChart: React.FC<WeatherTrendsChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="animate-pulse bg-gray-200 dark:bg-gray-700 w-full h-full rounded-lg"></div>
      </div>
    );
  }

  const chartData = useMemo(() => 
    data.map(item => ({
      time: new Date(item.timestamp).toLocaleTimeString(),
      irradiance: Math.round(item.irradiance),
      temperature: Math.round(item.temperature),
      wind: Math.round(item.wind_speed),
      humidity: Math.round(item.humidity)
    })), [data]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg">
          <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}{getUnit(entry.dataKey)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getUnit = (dataKey: string) => {
    switch (dataKey) {
      case 'irradiance': return ' W/m²';
      case 'temperature': return '°C';
      case 'wind': return ' m/s';
      case 'humidity': return '%';
      default: return '';
    }
  };

  return (
    <div className="w-full h-64 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="2 2" className="opacity-30" stroke="currentColor" />
          <XAxis 
            dataKey="time" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: 'currentColor' }}
            className="text-gray-600 dark:text-gray-400"
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: 'currentColor' }}
            className="text-gray-600 dark:text-gray-400"
          />
          <Tooltip content={<CustomTooltip />} />
          <Line 
            type="monotone" 
            dataKey="irradiance" 
            stroke="#f59e0b" 
            strokeWidth={2} 
            dot={false} 
            name="Irradiance"
            animationDuration={400}
          />
          <Line 
            type="monotone" 
            dataKey="temperature" 
            stroke="#ef4444" 
            strokeWidth={2} 
            dot={false} 
            name="Temperature"
            animationDuration={400}
          />
          <Line 
            type="monotone" 
            dataKey="wind" 
            stroke="#06b6d4" 
            strokeWidth={2} 
            dot={false} 
            name="Wind Speed"
            animationDuration={400}
          />
          <Line 
            type="monotone" 
            dataKey="humidity" 
            stroke="#10b981" 
            strokeWidth={2} 
            dot={false} 
            name="Humidity"
            animationDuration={400}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default WeatherTrendsChart;